import React from 'react'
import { notification } from 'antd'
import moment from 'moment-timezone'
import _ from 'lodash'
import {
  getPartnerCategoryList,
  getWWFeatures,
  getZumbaPreviewStartDate,
  setZumbaPreviewStartDate,
} from './wwLocalStorage'
import { getPartnerId } from './authLocalStorage'
import { getGTMID } from 'config'

export const showNotification = ({ type, title, message }) => {
  notification[type]({
    message: title,
    description: message,
  })
}

export const showBottomNotification = ({
  title,
  message,
  placement = 'bottomRight',
  className,
}) => {
  notification.open({
    message: title,
    description: message,
    placement: placement,
    style: {
      background: '#444444',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
      borderRadius: '4px',
      color: 'white',
      fontSize: '16px',
      marginLeft: '20px',
    },
    duration: 1.5,
    className: className,
  })
}

export const lazyImport = (view) => {
  try {
    return React.lazy(() => retry(() => view()))
  } catch (e) {
    return null
  }
}

const retry = (fn, retriesLeft = 5, interval = 1000) => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            reject(error)
          } else {
            retry(fn, retriesLeft - 1, interval).then(resolve, reject)
          }
        }, interval)
      })
  })
}

export const addGoogleTagManager = (partnerId) => {
  const gtmId = getGTMID(partnerId)
  if (gtmId) {
    const script = document.createElement('script')
    script.innerHTML = `(function (w, d, s, l, i) {
        w[l] = w[l] || []
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != 'dataLayer' ? '&l=' + l : ''
        j.async = true
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
        f.parentNode.insertBefore(j, f)
      })(window, document, 'script', 'dataLayer', '${gtmId}')`
    document.head.appendChild(script)

    const noScript = document.createElement('noscript')
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`
    iframe.height = 0
    iframe.width = 0
    iframe.style = 'display: none; visibility: hidden'
    noScript.appendChild(iframe)
    document.body.appendChild(noScript)
  }
}

export const PARTNER_ID = Object.freeze({
  WW: 1,
  NOOM: 19,
  NOOM_VIBE: 20,
  REVIVE: 21,
})

export const postNativeMessage = (message) => {
  try {
    if (window.webkit) {
      window.webkit.messageHandlers.nativeProcess.postMessage(message)
    } else {
      // eslint-disable-next-line no-undef
      nativeProcess.postMessage(JSON.stringify(message))
    }
  } catch (e) {
    console.error(e)
    if (process.env.REACT_APP_ENV === 'staging') console.error(message)
  }
}

export const postIframeMessage = (message) => {
  try {
    window?.parent?.postMessage?.(message, '*')
  } catch (e) {
    console.error(e)
  }
}

export const isMuscleDefense = () => {
  const features = getFeatures()
  return features.includes('muscledefense')
}

export const isMuscleDefenseChallenge = (challenge) => {
  return (challenge?.name || '').includes('Muscle Defense')
}

export const filterPartnerCategory = (categoryList) => {
  const partnerCategoryList = getPartnerCategoryList()
  return _.filter(categoryList || [], (category) =>
    _.find(
      partnerCategoryList || [],
      (item) => item.categoryValue === category.categoryValue
    )
  )
}

export const isPremiumUser = () => {
  const features = getFeatures()
  return features.includes('premium')
}

export const isDarkMode = () => {
  return +getPartnerId() === PARTNER_ID.NOOM_VIBE
}

export const isNoomUser = () => {
  return +getPartnerId() === PARTNER_ID.NOOM
}

export const showAdForPremium = (minDays, maxDays) => {
  let zumbaPreviewStartDate = getZumbaPreviewStartDate()
  const today = moment().format('YYYY-MM-DD')
  if (!zumbaPreviewStartDate) {
    setZumbaPreviewStartDate(today)
    zumbaPreviewStartDate = today
  }
  const duration = moment(today) - moment(zumbaPreviewStartDate)
  const days = moment.duration(duration).asDays()
  return (
    !isPremiumUser() &&
    +getPartnerId() === PARTNER_ID.NOOM &&
    days >= minDays &&
    days < maxDays &&
    !isPremiumUpsellUser()
  )
}

export const isPremiumUpsellUser = () => {
  const features = getFeatures()
  return (
    +getPartnerId() === PARTNER_ID.NOOM &&
    (features.includes('medupselleligible') ||
      features.includes('premiumupselleligible'))
  )
}

export const isPremiumUpsellEligible = () => {
  const features = getFeatures()
  return (
    +getPartnerId() === PARTNER_ID.NOOM &&
    features.includes('premiumupselleligible')
  )
}

export const getFeatures = () => {
  return (getWWFeatures() || '').split(',')
}

export const getErrorPagePath = () => {
  const isRevive = window.location.pathname?.includes('/revive')
  if (isRevive) {
    return '/partner/revive/error'
  } else {
    return '/partner/error'
  }
}
